import {curry} from "../global/functionalTools";
import {Vector2} from "./delme_globals";

export class MathUtils {

    public static checkLineIntersection(lineOneStart:Vector2, lineOneEnd:Vector2, lineTwoStart:Vector2, lineTwoEnd:Vector2):{x,y,onLine1, onLine2} {
        // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
        const result = {
            x: null as null|number,
            y: null as null|number,
            onLine1: false,
            onLine2: false
        };
        const denominator = ((lineTwoEnd.y - lineTwoStart.y) * (lineOneEnd.x - lineOneStart.x)) - ((lineTwoEnd.x - lineTwoStart.x) * (lineOneEnd.y - lineOneStart.y));
        if (denominator === 0) {
            return result;
        }
        let a = lineOneStart.y - lineTwoStart.y;
        let b = lineOneStart.x - lineTwoStart.x;
        const numerator1 = ((lineTwoEnd.x - lineTwoStart.x) * a) - ((lineTwoEnd.y - lineTwoStart.y) * b);
        const numerator2 = ((lineOneEnd.x - lineOneStart.x) * a) - ((lineOneEnd.y - lineOneStart.y) * b);
        a = numerator1 / denominator;
        b = numerator2 / denominator;

        // if we cast these lines infinitely in both directions, they intersect here:
        result.x = lineOneStart.x + (a * (lineOneEnd.x - lineOneStart.x));
        result.y = lineOneStart.y + (a * (lineOneEnd.y - lineOneStart.y));
        // if line1 is a segment and line2 is infinite, they intersect if:
        if (a >= 0 && a <= 1) {
            result.onLine1 = true;
        }
        // if line2 is a segment and line1 is infinite, they intersect if:
        if (b >= 0 && b <= 1) {
            result.onLine2 = true;
        }
        // if line1 and line2 are segments, they intersect if both of the above are true
        return result;
    }

    public static getBoxCorners(boxCenter, width, height):{topleft, topright, bottomleft, bottomright} {
        return {
            topleft: {x: boxCenter.x - width / 2, y: boxCenter.y + height / 2},
            topright: {x: boxCenter.x + width / 2, y: boxCenter.y + height / 2},
            bottomleft: {x: boxCenter.x - width / 2, y: boxCenter.y - height / 2},
            bottomright: {x: boxCenter.x + width / 2, y: boxCenter.y - height / 2}
        };
    }

    public static pointsAreInRectange(pointA:Vector2, pointB:Vector2, corners:{topleft, topright, bottomleft, bottomright}):boolean {
        return pointA.x < corners.topright.x && pointA.x > corners.topleft.x &&
            pointA.y < corners.topleft.y && pointA.y > corners.bottomleft.y &&
            pointB.x < corners.topright.x && pointB.x > corners.topleft.x &&
            pointB.y < corners.topleft.y && pointB.y > corners.bottomleft.y;
    }

    public static pointIsInRectangle(pointA:Vector2, corners:{topleft, topright, bottomleft, bottomright}):boolean {
        return pointA.x < corners.topright.x && pointA.x > corners.topleft.x &&
            pointA.y < corners.topleft.y && pointA.y > corners.bottomleft.y;
    }




    /**
     *Berechnet, ob eine Linie eine Box schneidet. Gibt auch wahr zurück, wenn die Linie von A nach B komplett in der Box liegt
     */
    public static lineIntersectsBox(strecke: {pointA, pointB}, box:{translation:Vector2, width, height}):boolean {
        const corners = MathUtils.getBoxCorners(box.translation, box.width, box.height);
        let result;
        if(MathUtils.pointIsInRectangle(strecke.pointA, corners) || MathUtils.pointIsInRectangle(strecke.pointB, corners)) {
        // if(MathUtils.pointsAreInRectange(strecke.pointA, strecke.pointB, corners)) {
            return true;
        }
        const lineIntersects = curry(MathUtils.checkLineIntersection, strecke.pointA, strecke.pointB);

        result = lineIntersects(
            corners.topleft as Vector2,
            corners.topright as Vector2
        );
        if (result.onLine1 && result.onLine2) {
            return true;
        }
        result = lineIntersects(
            corners.topleft as Vector2,
            corners.bottomleft as Vector2
        );
        if (result.onLine1 && result.onLine2) {
            return true;
        }
        result = lineIntersects(
            corners.bottomright as Vector2,
            corners.bottomleft as Vector2
        );
        if (result.onLine1 && result.onLine2) {
            return true;
        }
        result = lineIntersects(
            corners.bottomright as Vector2,
            corners.topright as Vector2
        );
        return result.onLine1 && result.onLine2;
    }

    public static rotateVector(vector:Vector2, angle:number):Vector2 {
        const vec = new Vector2();
        vec.x = vector.x * Math.cos(angle) - vector.y * Math.sin(angle);
        vec.y = vector.y * Math.cos(angle) + vector.x * Math.sin(angle);

        return vec;
    }

    public static distSquared(v:Vector2, w:Vector2):number {
        return Math.pow((v.x - w.x), 2) + Math.pow((v.y - w.y), 2);
    }

    /** Funktion, die den Abstand im Quadrat berechnet. Da Wurzelziehen recht aufwändig ist, wird das erst ganz am Schluss gemacht*/
    public static distToSegmentSquared(p:Vector2, lineStart:Vector2, lineEnd:Vector2):{point, distance, distanceTo} {
        const streckenlaenge = this.distSquared(lineStart, lineEnd);
        if (streckenlaenge === 0) {
            return {point: lineStart, distance: this.distSquared(p, lineStart), distanceTo: "firstPoint"};
        }
        const t = ((p.x - lineStart.x) * (lineEnd.x - lineStart.x) + (p.y - lineStart.y) * (lineEnd.y - lineStart.y)) / streckenlaenge;
        if (t < 0) {
            return {point: lineStart, distance: this.distSquared(p, lineStart), distanceTo: "firstPoint"};
        }
        if (t > 1) {
            return {point: lineEnd, distance: this.distSquared(p, lineEnd), distanceTo: "secondPoint"};
        }
        return {
            point: {
                x: lineStart.x + t * (lineEnd.x - lineStart.x),
                y: lineStart.y + t * (lineEnd.y - lineStart.y)
            },
            distance: this.distSquared(p, new Vector2(lineStart.x + t * (lineEnd.x - lineStart.x),lineStart.y + t * (lineEnd.y - lineStart.y))),
            distanceTo: "line"
        };
    }
    /**
     * Berechnet die Distanz eines Punktes P zur Strecke
     */
    public static distToSegment(p:Vector2, lineStart:Vector2, lineEnd:Vector2):{point, distance, distanceTo} {

        const result = this.distToSegmentSquared(p, lineStart, lineEnd);
        result.distance = Math.floor(Math.sqrt(result.distance));
        return result;
    }
    /**
     * Gibt an, ob ein Punkt C "links" (in negative x Richtung) der Geraden AB liegt. Benötigt z.B. um herauszufinden,
     * ob ein Winkel über 180 Grad ist
     */
    public static isLeft(lineStart, lineEnd, point):boolean {
        return ((lineEnd.x - lineStart.x) * (point.y - lineStart.y) - (lineEnd.y - lineStart.y) * (point.x - lineStart.x)) > 0;
    }

    public static find_angleBogenmass(A:Vector2, B:Vector2, C:Vector2):number {
        const over180 = this.isLeft(A, B, C);
        const AB = MathUtils.vectorLength2D(A, B);
        const BC = MathUtils.vectorLength2D(B, C);
        const AC = MathUtils.vectorLength2D(A, C);
        if (over180) {
            return Math.PI * 2 + Math.acos((BC * BC + AB * AB - AC * AC) / (2 * BC * AB)); // (a^2 + b^2 - c^2) / (2ab)
            //360 in rad = 2 * PI
        }
        return -Math.acos((BC * BC + AB * AB - AC * AC) / (2 * BC * AB)); //Vorzeichenwechsel, da wir jetzt die y Koordinaten umgedreht haben...

    }

    public static vectorLength2D(A:Vector2, B:Vector2) {
        return Math.sqrt(Math.pow(B.x - A.x, 2) + Math.pow(B.y - A.y, 2));
    }

    public static getNextPowerOfTwo(value:number):number {
        let pow = 1;
        while (pow < value) {
            pow *= 2;
        }
        return pow;
    }
}
